// ===========================================
// Admin Save the Dates Management Page
// ===========================================
// List and manage Save the Dates with status tabs and row actions.

import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Skeleton,
  Tooltip,
  Alert,
  TablePagination,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  useSaveTheDates,
  useDeleteSaveTheDate,
  useResendSaveTheDate,
} from '../../hooks/useSaveTheDates';
import type { SaveTheDate, SaveTheDateStatus, SaveTheDateChannel } from '../../api/saveTheDates.api';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';

// ===========================================
// STATUS CONFIGURATION
// ===========================================

const statusConfig: Record<SaveTheDateStatus, { label: string; bgcolor: string; color: string }> = {
  DRAFT: { label: 'Draft', bgcolor: '#9DA5AF', color: 'white' },
  SCHEDULED: { label: 'Scheduled', bgcolor: '#FFCE00', color: '#080808' },
  SENT: { label: 'Sent', bgcolor: '#96DAC9', color: '#080808' },
  FAILED: { label: 'Failed', bgcolor: '#ff4040', color: 'white' },
};

const CHANNEL_LABELS: Record<SaveTheDateChannel, string> = {
  EMAIL: 'Email',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
};

type StatusFilterValue = 'ALL' | SaveTheDateStatus;

const statusTabs: { value: StatusFilterValue; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
];

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function SaveTheDatesPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();

  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [resendTargetId, setResendTargetId] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search
  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimerRef.current !== null) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(0);
    }, 400);
  };

  const {
    data: items,
    isLoading,
    isError,
    error,
    refetch,
  } = useSaveTheDates(
    statusFilter === 'ALL' ? undefined : { status: statusFilter }
  );

  const deleteMutation = useDeleteSaveTheDate();
  const resendMutation = useResendSaveTheDate();

  // Client-side search filter (the list API doesn't paginate, so filter locally)
  const allItems: SaveTheDate[] = items ?? [];
  const filtered = debouncedSearch
    ? allItems.filter((s) =>
        s.title.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : allItems;

  const totalCount = filtered.length;
  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleEditClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigate(`/${schoolSlug}/admin/save-the-dates/${id}`);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleteTargetId(id);
  };

  // List-page Resend = plain resend (same content). To amend, open the editor
  // and use the Amend toggle there.
  const handleResendClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setResendTargetId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    await deleteMutation.mutateAsync(deleteTargetId);
    setDeleteTargetId(null);
  };

  const handleResendConfirm = async () => {
    if (!resendTargetId) return;
    await resendMutation.mutateAsync(resendTargetId);
    setResendTargetId(null);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: StatusFilterValue) => {
    setStatusFilter(newValue);
    setPage(0);
  };

  return (
    <Box>
      <PageHeader
        title="Save the Dates"
        subtitle="Send early announcements to families before an event is ready"
        actionLabel="New Save the Date"
        actionIcon={<AddIcon />}
        onAction={() => navigate(`/${schoolSlug}/admin/save-the-dates/new`)}
      />

      {/* Filters */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {statusTabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>

        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search by title..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} size="small">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error & { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'Failed to load save the dates. Please try again.'}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Event Date</TableCell>
                <TableCell>Scheduled For</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell>Channels</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}
                    >
                      <EventIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography color="text.secondary">
                        {debouncedSearch || statusFilter !== 'ALL'
                          ? 'No save the dates match your filters'
                          : 'No save the dates yet'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((item) => {
                  const cfg = statusConfig[item.status];
                  const canDelete =
                    item.status === 'DRAFT' || item.status === 'SCHEDULED';
                  const canResend = item.status === 'SENT';

                  return (
                    <TableRow
                      key={item.id}
                      hover
                      onClick={() => navigate(`/${schoolSlug}/admin/save-the-dates/${item.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cfg.label}
                          size="small"
                          sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.eventDate
                            ? format(new Date(item.eventDate), 'dd MMM yyyy, h:mm a')
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.scheduledFor
                            ? format(new Date(item.scheduledFor), 'dd MMM yyyy, h:mm a')
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.sentAt
                            ? format(new Date(item.sentAt), 'dd MMM yyyy, h:mm a')
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {item.channels.map((ch) => (
                            <Chip
                              key={ch}
                              label={CHANNEL_LABELS[ch]}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditClick(e, item.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canResend && (
                            <Tooltip title="Resend (same content)">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => handleResendClick(e, item.id)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => handleDeleteClick(e, item.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTargetId}
        title="Delete Save the Date"
        message="Are you sure you want to delete this Save the Date? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTargetId(null)}
      />

      {/* Resend Confirm (same content) */}
      <ConfirmDialog
        open={!!resendTargetId}
        title="Resend Save the Date"
        message="Re-send this Save the Date (unchanged) to all recipients? To change details, open it and use Amend."
        confirmLabel="Resend"
        confirmColor="primary"
        loading={resendMutation.isPending}
        onConfirm={handleResendConfirm}
        onCancel={() => setResendTargetId(null)}
      />
    </Box>
  );
}
